// The local event envelope — how a CLI step reports itself to a program.
//
// WHY IT EXISTS. The install's cloud half has been machine-readable for a while
// (the status document). Its LOCAL half — preflight, launch, the finalize that
// runs inside `status --watch` — spoke only prose. A wrapper driving the install
// had two choices: scrape that prose, or not know. Scraping prose is the paste
// runbook's fragility wearing a new coat, and finalize being invisible is worse
// than cosmetic: finalize is what registers the sign-in redirect URI, so a
// wrapper that reports "done" when the cloud half finishes is reporting a
// platform the founder may not be able to sign in to.
//
// ONE ENVELOPE, TWO TRANSPORTS. Quick commands (`preflight`, `profile`, a plain
// `status`) emit ONE final document; long-running ones (`launch`,
// `status --watch`) emit one event per line as they go, the last line being the
// terminal event. Same envelope either way, so a wrapper has one parser rather
// than one per command. See docs/install-contract.md for the table.
//
// THE SAME ENVELOPE CARRIES THE CLOUD PHASES TOO. `status --watch` relays what
// it reads from the status document as events in this shape, so a wrapper sees
// one continuous stream across the local/cloud seam instead of two vocabularies
// meeting in the middle.

import {
  any, enom, lit, map, obj, opt, req, str,
  toJsonSchema, validateNode,
  type SpecNode, type ValidateOptions, type Violation,
} from "./spec.js";

/** The schema version this package declares for local events. */
export const EVENT_SCHEMA_VERSION = 1;

/** Where a step is in its life. `ok`, `failed` and `skipped` are terminal. */
export type EventStatus = "running" | "ok" | "failed" | "skipped";

/** How loud the event is. See the invariant below — this is not free-form. */
export type EventLevel = "info" | "warn" | "error";

/** Commands that speak this envelope. */
export const EVENT_COMMANDS = [
  "bootstrap.preflight",
  "bootstrap.launch",
  "bootstrap.profile",
  "bootstrap.status",
] as const;
export type EventCommand = (typeof EVENT_COMMANDS)[number];

/**
 * Commands that cannot run before an install exists, and therefore always know
 * the id. `launch` mints it; `status` reads an install that must already be in
 * flight. `preflight` and `profile` are deliberately absent — both are
 * documented as runnable standalone, so requiring an id there would refuse a
 * legitimate use rather than catch a defect.
 */
export const COMMANDS_REQUIRING_INSTALL_ID: ReadonlySet<EventCommand> = new Set<EventCommand>([
  "bootstrap.launch",
  "bootstrap.status",
]);

/**
 * Step slugs the CLI reserves for its own local steps.
 *
 * `status --watch` relays the installer's cloud PHASES as `step` too, so the two
 * namespaces share a field. These are the ones a consumer can rely on meaning a
 * local step; anything else on a `bootstrap.status` stream is a cloud phase from
 * the phase registry. A future phase named one of these would collide, which is
 * why they are written down rather than left to convention.
 */
export const RESERVED_LOCAL_STEPS = ["preflight", "launch", "profile", "install", "finalize", "installer-launch"] as const;

/**
 * One event.
 *
 * `message` is human prose: safe to show a founder, never safe to parse. Every
 * machine decision should key off `step`, `status` and `code`, all of which are
 * stable slugs. This is the same discipline the status document's `detail`
 * field carries, for the same reason.
 */
export interface InstallEvent {
  schemaVersion: number;
  /** ISO-8601 UTC. */
  ts: string;
  command: EventCommand;
  /** Stable machine slug for the step, e.g. `app-registration`. Never prose. */
  step: string;
  status: EventStatus;
  level: EventLevel;
  /** Present on failure. Reuses the CLI's existing error codes. */
  code?: string;
  /** Human prose. Safe to show, never parse. */
  message?: string;
  /**
   * The install this event belongs to.
   *
   * OPTIONAL BEFORE IT IS KNOWN, REQUIRED ONCE IT IS.
   *
   * The dividing line is whether the command can run before an install exists,
   * not where it sits in the sequence. `preflight` checks a subscription and
   * `profile` records two answers — both are documented as runnable before any
   * launch, so both can legitimately have no id. `launch` mints it, and
   * `status` reads an install that must already exist, so for those two it is
   * required.
   *
   * Where it IS present it is load-bearing: it is what lets a consumer join this
   * local stream to the status document and to the phone-home wire without
   * inferring the association from ordering. The same id already threads page →
   * container → wire; local events join that family rather than standing
   * outside it.
   */
  installId?: string;
  /** Step-specific payload. Declared per step by the command that emits it. */
  data?: Record<string, unknown>;
}

/**
 * THE LEVEL INVARIANT, and why `level` survives alongside `status`.
 *
 * Two fields that can express severity can disagree, and a consumer then has to
 * pick one. So level is pinned wherever the answer is already determined:
 *
 *   status `failed`          ⇒ level MUST be `error`
 *   status `ok` / `skipped`  ⇒ level MUST be `info`
 *   status `running`         ⇒ level is free
 *
 * The free case is the whole reason to keep the field: `running` + `warn` is a
 * mid-step advisory — something went sideways, the step is continuing, and the
 * founder should know. Nothing else in the envelope can say that. It is also
 * the surface the installer's `warnings` array has always lacked: warnings are
 * declared in the status document but rendered nowhere, and they arrive here as
 * `running` + `warn` events.
 */
export function levelViolatesInvariant(status: EventStatus, level: EventLevel): string | null {
  if (status === "failed" && level !== "error") {
    return `a failed step must be level "error", got "${level}"`;
  }
  if ((status === "ok" || status === "skipped") && level !== "info") {
    return `a ${status} step must be level "info", got "${level}"`;
  }
  return null;
}

/** The field spec — single source for the validator and the JSON Schema. */
export const INSTALL_EVENT_SPEC: SpecNode = obj({
  schemaVersion: req(lit(EVENT_SCHEMA_VERSION), "Always 1 in this version of the contract."),
  ts: req(str(), "ISO-8601 UTC of the moment the step reported."),
  command: req(enom(...EVENT_COMMANDS), "Which command is speaking."),
  step: req(str(), "Stable machine slug. Key machine decisions off this, never off `message`."),
  status: req(enom("running", "ok", "failed", "skipped"), "`ok`, `failed` and `skipped` are terminal for the step."),
  level: req(enom("info", "warn", "error"), "Pinned by `status` except while running — see levelViolatesInvariant."),
  code: opt(str(), "Present on failure. Reuses the CLI's LocalCliError codes."),
  message: opt(str(), "Human prose. Safe to show, never parse."),
  installId: opt(str(), "Absent only before an install exists (preflight). Required from launch onward."),
  data: opt(map(any()), "Step-specific payload, shape owned by the emitting step."),
});

/** Why an event was rejected. */
export type EventRejection =
  | { reason: "not-json"; message: string }
  | { reason: "missing-schema-version" }
  | { reason: "unknown-schema-version"; found: unknown; supported: number }
  | { reason: "invalid"; violations: Violation[] };

export type EventParse =
  | { ok: true; event: InstallEvent }
  | { ok: false; rejection: EventRejection };

/**
 * Parse and validate one event.
 *
 * Same strictness split as the status document: unknown fields are rejected by
 * default (the CI guard) and ignored by `parseInstallEventForReader` (the
 * runtime one), because enforcing closure on a consumer would make every added
 * field break every wrapper already shipped. Unknown VERSIONS are refused
 * either way.
 */
export function parseInstallEvent(raw: string, opts: ValidateOptions = {}): EventParse {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw.trim());
  } catch (e) {
    return { ok: false, rejection: { reason: "not-json", message: (e as Error).message } };
  }
  const hasVersion =
    typeof parsed === "object" && parsed !== null &&
    Object.prototype.hasOwnProperty.call(parsed, "schemaVersion");
  if (!hasVersion) return { ok: false, rejection: { reason: "missing-schema-version" } };
  const version = (parsed as { schemaVersion?: unknown }).schemaVersion;
  if (version !== EVENT_SCHEMA_VERSION) {
    return { ok: false, rejection: { reason: "unknown-schema-version", found: version, supported: EVENT_SCHEMA_VERSION } };
  }
  const violations = [...validateNode(INSTALL_EVENT_SPEC, parsed, "", opts)];

  // The invariant is a relationship between two fields, which a per-field
  // schema cannot express — so it is enforced here, in the same pass, rather
  // than left as prose nobody runs.
  const e = parsed as Partial<InstallEvent>;
  if (typeof e.status === "string" && typeof e.level === "string") {
    const bad = levelViolatesInvariant(e.status, e.level);
    if (bad !== null) violations.push({ path: ".level", message: bad });
  }
  // The correlation rule, likewise: absent is legal only for commands that can
  // run before an install exists.
  if (e.command !== undefined && COMMANDS_REQUIRING_INSTALL_ID.has(e.command) && (e.installId === undefined || e.installId === "")) {
    violations.push({
      path: ".installId",
      message: `is required for "${String(e.command)}" and must be non-empty — it is what joins this stream to the status document and the wire`,
    });
  }

  if (violations.length > 0) return { ok: false, rejection: { reason: "invalid", violations } };
  return { ok: true, event: parsed as InstallEvent };
}

/** Parse the way a runtime consumer should: tolerant of fields it does not know. */
export function parseInstallEventForReader(raw: string): EventParse {
  return parseInstallEvent(raw, { unknownFields: "ignore" });
}

/** Parse a whole NDJSON stream, keeping line numbers for diagnosis. */
export function parseEventStream(text: string): { events: InstallEvent[]; errors: { line: number; rejection: EventRejection }[] } {
  const events: InstallEvent[] = [];
  const errors: { line: number; rejection: EventRejection }[] = [];
  text.split("\n").forEach((raw, i) => {
    if (raw.trim() === "") return;
    const r = parseInstallEventForReader(raw);
    if (r.ok) events.push(r.event);
    else errors.push({ line: i + 1, rejection: r.rejection });
  });
  return { events, errors };
}

/** Generated JSON Schema for one event. */
export function installEventJsonSchema(): Record<string, unknown> {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: "https://m8t.run/schemas/install/install-event.v1.json",
    title: "m8t install CLI event",
    description:
      "One event from the m8t CLI's machine surface. Long-running commands emit these as NDJSON on " +
      "stdout; quick commands emit a single final document. NOTE: the level/status invariant and the " +
      "installId rule are relationships between fields and are enforced by the validator, not by this " +
      "schema. Generated from packages/install-contract/src/events.ts — do not hand-edit.",
    ...toJsonSchema(INSTALL_EVENT_SPEC),
  };
}

/** The final document a quick command emits: the same envelope, plus its result. */
export interface InstallFinalDocument extends InstallEvent {
  status: "ok" | "failed" | "skipped";
}
